import * as React from 'react';
import { numberWithCommas, TankCircuit } from 'js-electrical-engineering-equations';
import { useCallback, useState } from 'react';
import { Button, Card, CardActions, CardContent, TextField } from '@material-ui/core';
import { useLastCalculated } from '../../hooks/useLastCalculated';

const fieldsDefaults = {
  frequencyInHertz: '',
  inductanceInNanoHenrys: '',
  capacitanceInPicoFarads: '',
};

export function TankCircuitComponent(): JSX.Element {
  const [fields, setFields] = useState(fieldsDefaults);

  const { lastCalculated, wasLastCalculated, setLastCalculated } = useLastCalculated<
    keyof typeof fieldsDefaults
  >();

  const clear = useCallback(() => {
    setLastCalculated([]);
    setFields(fieldsDefaults);
  }, [setLastCalculated, setFields]);

  const onChange = useCallback(
    (event) => {
      const field = event.target.name;
      const value = event.target.value;
      setFields({ ...fields, [field]: value });
    },
    [fields, setFields],
  );

  const calculate = useCallback(
    (e) => {
      e.preventDefault();
      const { frequencyInHertz, inductanceInNanoHenrys, capacitanceInPicoFarads } = fields;
      const inductance = parseFloat(fields.inductanceInNanoHenrys) / 1000000000;
      const capacitance = parseFloat(fields.capacitanceInPicoFarads) / 1000000000000;

      if (
        wasLastCalculated('frequencyInHertz') ||
        (inductanceInNanoHenrys && capacitanceInPicoFarads && frequencyInHertz === '')
      ) {
        const calculatedFrequencyInHertz = TankCircuit.calcFrequency(inductance, capacitance);
        setFields({
          ...fields,
          frequencyInHertz: numberWithCommas(calculatedFrequencyInHertz),
        });
        setLastCalculated(['frequencyInHertz']);
      } else if (
        wasLastCalculated('capacitanceInPicoFarads') ||
        (frequencyInHertz && inductanceInNanoHenrys && capacitanceInPicoFarads === '')
      ) {
        const frequencyAsFloat = parseFloat(frequencyInHertz.replace(/,/g, ''));
        const calculatedCapacitanceInFarads = TankCircuit.calcCapacitance(
          frequencyAsFloat,
          inductance,
        );

        setFields({
          ...fields,
          capacitanceInPicoFarads: `${calculatedCapacitanceInFarads * 1000000000000}`,
        });
        setLastCalculated(['capacitanceInPicoFarads']);
      } else if (
        wasLastCalculated('inductanceInNanoHenrys') ||
        (frequencyInHertz && capacitanceInPicoFarads && inductanceInNanoHenrys === '')
      ) {
        const frequencyAsFloat = parseFloat(frequencyInHertz.replace(/,/g, ''));
        const calculatedInductanceInHenrys = TankCircuit.calcInductance(
          frequencyAsFloat,
          capacitance,
        );

        setFields({
          ...fields,
          capacitanceInPicoFarads: `${calculatedInductanceInHenrys * 1000000000}`,
        });
        setLastCalculated(['inductanceInNanoHenrys']);
      }
    },
    [fields, lastCalculated, wasLastCalculated],
  );

  return (
    <form onSubmit={calculate}>
      <div>
        <Card>
          <CardContent>
            <div>
              <p>Supply at least two of the values to calculate the third.</p>
              <TextField
                label="&#x192; (Frequency in Hertz)"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={wasLastCalculated('frequencyInHertz')}
                fullWidth
                name="frequencyInHertz"
                margin="normal"
                onChange={onChange}
                value={fields.frequencyInHertz}
              />
              <TextField
                label="L (Inductance in nH)"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={wasLastCalculated('inductanceInNanoHenrys')}
                fullWidth
                name="inductanceInNanoHenrys"
                margin="normal"
                onChange={onChange}
                value={fields.inductanceInNanoHenrys}
              />
              <TextField
                label="C (Capacitance in pF)"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={wasLastCalculated('capacitanceInPicoFarads')}
                fullWidth
                name="capacitanceInPicoFarads"
                margin="normal"
                onChange={onChange}
                value={fields.capacitanceInPicoFarads}
              />
            </div>
          </CardContent>
          <CardActions>
            <Button type="button" tabIndex="6" onClick={clear}>
              Clear
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              style={{ marginLeft: 'auto' }}
            >
              Calculate
            </Button>
          </CardActions>
        </Card>
      </div>
    </form>
  );
}
